import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";

import { agentQueryKey } from "./useAgent";

interface DeleteAgentParams {
  orgId: string;
  agentId: string;
}

/**
 * Mutation hook for deleting an agent.
 *
 * Invalidates the list query so the Agents tab refetches; the per-agent
 * detail query is removed (not just invalidated) so a stale detail page
 * won't briefly render the now-deleted record before its own 404 lands.
 */
export function useDeleteAgent() {
  const { agentService } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<void, Error, DeleteAgentParams>({
    mutationFn: ({ orgId, agentId }) =>
      agentService.deleteAgent(agentId, { orgId }),
    onSuccess: (_, { agentId }) => {
      void queryClient.invalidateQueries({ queryKey: ["agents"] });
      queryClient.removeQueries({ queryKey: agentQueryKey(agentId) });
    },
  });
}
